import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Mail, Phone, MapPin, Briefcase, MessageSquare, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/apiClient";
import { formatBizDateTime } from "../../lib/formatBiz";

const STAGES = [
  { key: "new_lead", label: "New lead" },
  { key: "contacted", label: "Contacted" },
  { key: "quoted", label: "Quoted" },
  { key: "scheduled", label: "Scheduled" },
  { key: "job_complete", label: "Job complete" },
  { key: "repeat_recurring", label: "Repeat/Recurring" },
];

export default function ContactDetail() {
  const { contactId } = useParams();
  const [data, setData] = useState(null);
  const [msgBody, setMsgBody] = useState("");
  const [channel, setChannel] = useState("email");
  const [sending, setSending] = useState(false);

  const load = () => { api.get(`/contacts/${contactId}`).then(r => setData(r.data)); };
  useEffect(() => { load(); }, [contactId]);

  const setStage = async (stage) => {
    try {
      await api.patch(`/contacts/${contactId}`, { stage });
      toast.success("Stage updated");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const send = async () => {
    if (!msgBody) return;
    setSending(true);
    try {
      await api.post("/inbox/send", { contact_id: contactId, channel, body: msgBody, subject: channel === "email" ? "A note from Wonder Washers" : "" });
      toast.success(`${channel === "sms" ? "Text" : "Email"} sent`);
      setMsgBody("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  if (!data) return <div className="text-slate-500">Loading…</div>;
  const { contact, jobs, messages } = data;

  return (
    <div className="space-y-6">
      <Link to="/admin/contacts" className="text-sm font-semibold text-[color:var(--owc-sky)] inline-flex items-center gap-1"><ArrowLeft className="h-3 w-3" /> All contacts</Link>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-4">
          <div className="owc-card p-6">
            <div className="font-[Outfit] font-extrabold text-2xl text-[color:var(--owc-navy)]">{contact.name}</div>
            <div className="mt-3 space-y-2 text-sm text-slate-700">
              {contact.email && <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-[color:var(--owc-sky)]" /> {contact.email}</div>}
              {contact.phone && <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-[color:var(--owc-sky)]" /> {contact.phone}</div>}
              {contact.address && <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[color:var(--owc-sky)]" /> {contact.address}, {contact.city} {contact.zip}</div>}
            </div>
            <div className="mt-5">
              <div className="text-xs font-bold uppercase text-slate-500 mb-2">Pipeline stage</div>
              <select className="owc-input" value={contact.stage} onChange={e => setStage(e.target.value)}>
                {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-2 text-xs">
              <div className="owc-badge-slate w-full justify-center">Source: {contact.source}</div>
              <div className="owc-badge-green w-full justify-center">${(contact.lifetime_value || 0).toFixed(0)} LTV</div>
              <div className={contact.consent_sms ? "owc-badge-blue" : "owc-badge-red"}>SMS: {contact.consent_sms ? "opted-in" : "no"}</div>
              <div className={contact.consent_email ? "owc-badge-blue" : "owc-badge-red"}>Email: {contact.consent_email ? "opted-in" : "no"}</div>
            </div>
            {contact.notes && (
              <div className="mt-5 border-t border-slate-100 pt-4">
                <div className="text-xs font-bold uppercase text-slate-500 mb-1">Notes</div>
                <div className="text-sm text-slate-700 whitespace-pre-line">{contact.notes}</div>
              </div>
            )}
          </div>

          <div className="owc-card p-6">
            <div className="font-[Outfit] font-bold text-[color:var(--owc-navy)] mb-3 flex items-center gap-2"><Send className="h-4 w-4 text-[color:var(--owc-sky)]" /> Send a message</div>
            <select className="owc-input mb-2" value={channel} onChange={e => setChannel(e.target.value)}>
              <option value="email">Email</option>
              <option value="sms">SMS</option>
            </select>
            <textarea className="owc-input min-h-[80px]" placeholder={channel === "sms" ? "Keep it under 160 chars for one segment" : "Message"} value={msgBody} onChange={e => setMsgBody(e.target.value)} />
            <button disabled={sending || !msgBody} onClick={send} className="owc-btn-accent text-sm mt-3 inline-flex items-center gap-2">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send
            </button>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-4">
          <div className="owc-card p-6">
            <div className="font-[Outfit] font-bold text-[color:var(--owc-navy)] mb-3 flex items-center gap-2"><Briefcase className="h-4 w-4 text-[color:var(--owc-sky)]" /> Jobs</div>
            {jobs.length === 0 ? (
              <div className="text-slate-500 text-sm">No jobs yet.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {jobs.map(j => (
                  <li key={j.id} className="py-3 flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-[color:var(--owc-navy)]">{j.service_name}</div>
                      <div className="text-xs text-slate-500">{formatBizDateTime(j.scheduled_at)}</div>
                    </div>
                    <span className={`owc-badge-${j.status === "completed" ? "green" : j.status === "cancelled" ? "red" : "blue"}`}>{j.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="owc-card p-6">
            <div className="font-[Outfit] font-bold text-[color:var(--owc-navy)] mb-3 flex items-center gap-2"><MessageSquare className="h-4 w-4 text-[color:var(--owc-sky)]" /> Timeline</div>
            {messages.length === 0 ? (
              <div className="text-slate-500 text-sm">No messages yet.</div>
            ) : (
              <ol className="relative border-l-2 border-slate-100 pl-6 space-y-5">
                {messages.map(m => (
                  <li key={m.id} className="relative">
                    <span className={`absolute -left-[29px] top-1 h-3 w-3 rounded-full ${m.direction === "inbound" ? "bg-[color:var(--owc-sky)]" : "bg-[color:var(--owc-navy)]"}`} />
                    <div className="text-xs font-bold uppercase text-slate-500">{m.channel} · {m.direction} · <span className="normal-case">{new Date(m.created_at).toLocaleString()}</span></div>
                    {m.subject && <div className="font-semibold text-[color:var(--owc-navy)] mt-1">{m.subject}</div>}
                    <div className="text-sm text-slate-700 mt-1 whitespace-pre-line">{m.body}</div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
